import type { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { message, Select, TreeSelect } from 'antd';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Breadcrumbs from '../../components/Breadcrumbs';
import { PersonIcon, CheckCircleIcon } from '../../assets/icons';
import {
  useGetAdminOfficialQuery,
  useCreateAdminOfficialMutation,
  useUpdateAdminOfficialMutation,
  officialName,
  officialEmail,
  officialPhone,
  officialDesignation,
  officialDepartment,
  officialLevel,
} from '../../features/adminOfficials/adminOfficialsApi';
import {
  useGetOrganizationsQuery,
  useGetNodeTreeQuery,
  type OrgNode,
} from '../../features/organizations/organizationsApi';
/** A node in the Administrative Level TreeSelect (value = the level name). */
type LevelNode = { title: string; value: string; children?: LevelNode[] };

const INPUT =
  'w-full rounded-lg border bg-white px-4 py-2.5 text-sm text-ink placeholder:text-[#9ca3af] transition-colors focus:outline-none focus:ring-2';
const INPUT_OK = 'border-[#e2e8f0] focus:border-brand focus:ring-brand/15';
const INPUT_ERR = 'border-[#ef4444] focus:border-[#ef4444] focus:ring-red-500/15';
const inputCls = (err?: string) => [INPUT, err ? INPUT_ERR : INPUT_OK].join(' ');

interface AdminUserForm {
  fullName: string;
  email: string;
  contact: string;
  designation: string;
  department: string;
  /** Scopes the Administrative Level dropdown; not saved on the official. */
  organization: number | '';
  level: string;
}

const validationSchema = Yup.object({
  fullName: Yup.string().trim().required('Full name is required'),
  email: Yup.string().trim().email('Enter a valid email address').required('Email address is required'),
  contact: Yup.string()
    .required('Contact number is required')
    .test('phone', 'Enter a valid 10-digit number', (v) => !!v && /^\d{10}$/.test(v.replace(/\s+/g, ''))),
  designation: Yup.string().trim().required('Designation is required'),
  department: Yup.string().trim().required('Department is required'),
  organization: Yup.mixed<number | ''>().test(
    'org',
    'Organization is required',
    (v) => typeof v === 'number',
  ),
  level: Yup.string().trim().required('Administrative level is required'),
});

function Field({
  label,
  required,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string;
  children: ReactNode;
}) {
  return (
    <label className="flex flex-col gap-2">
      <span className="text-sm font-medium text-ink">
        {label}
        {required && <span className="text-[#ef4444]"> *</span>}
      </span>
      {children}
      {error && <span className="text-xs text-[#ef4444]">{error}</span>}
    </label>
  );
}

export default function CreateAdminUserPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const goBack = () => navigate('/admin/directory');

  const { data: editOfficial } = useGetAdminOfficialQuery(id!, { skip: !isEdit });
  const [createOfficial, { isLoading: isCreating }] = useCreateAdminOfficialMutation();
  const [updateOfficial, { isLoading: isUpdating }] = useUpdateAdminOfficialMutation();
  const saving = isCreating || isUpdating;

  const formik = useFormik<AdminUserForm>({
    enableReinitialize: true,
    initialValues: {
      fullName: editOfficial ? officialName(editOfficial) : '',
      email: editOfficial ? officialEmail(editOfficial) : '',
      contact: editOfficial ? officialPhone(editOfficial) : '',
      designation: editOfficial ? officialDesignation(editOfficial) : '',
      department: editOfficial ? officialDepartment(editOfficial) : '',
      organization: (editOfficial?.organization ?? '') as number | '',
      level: editOfficial ? officialLevel(editOfficial) : '',
    },
    validationSchema,
    onSubmit: async (values) => {
      const payload = {
        full_name: values.fullName.trim(),
        email: values.email.trim(),
        contact: values.contact.trim(),
        designation: values.designation.trim(),
        department: values.department.trim(),
        administrative_level: values.level.trim(),
        organization: Number(values.organization),
      };
      try {
        if (isEdit && id) {
          await updateOfficial({ id, patch: payload }).unwrap();
          message.success('Admin official updated.');
        } else {
          await createOfficial(payload).unwrap();
          message.success('Admin official created.');
        }
        goBack();
      } catch {
        message.error(`Could not ${isEdit ? 'update' : 'create'} the admin official. Please try again.`);
      }
    },
  });

  const { values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue } = formik;
  const errorOf = (name: keyof AdminUserForm) =>
    touched[name] && errors[name] ? (errors[name] as string) : undefined;

  // Organizations (to scope the admin-level list) + that org's administrative-hierarchy tree.
  const { data: orgs } = useGetOrganizationsQuery({ all: true });
  const { data: adminTree } = useGetNodeTreeQuery(
    { organization: values.organization as number, kind: 'admin' },
    { skip: !values.organization },
  );
  // Build the Administrative Level TreeSelect from the nested admin hierarchy. The stored
  // value is the level name (matches the `administrative_level` payload).
  const nodeLabel = (n: OrgNode) => n.org_name || n.level_name || String(n.id ?? '');
  const toTreeData = (nodes: OrgNode[]): LevelNode[] =>
    nodes.map((n) => {
      const label = nodeLabel(n);
      return {
        title: label,
        value: label,
        children: n.children?.length ? toTreeData(n.children) : undefined,
      };
    });
  const levelTreeData = toTreeData(adminTree ?? []);
  // In edit mode the saved level should still show even before an org is picked.
  const hasSaved = (data: LevelNode[]): boolean =>
    data.some((d) => d.value === values.level || (d.children && hasSaved(d.children)));
  if (values.level && !hasSaved(levelTreeData)) {
    levelTreeData.unshift({ title: values.level, value: values.level });
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex flex-col gap-1">
        <Breadcrumbs
          items={[
            { label: 'Admin Level Directory', to: '/admin/directory' },
            { label: isEdit ? 'Edit Admin User' : 'Create Admin User' },
          ]}
        />
        <h1 className="pt-1 text-2xl font-semibold text-ink">
          {isEdit ? 'Edit Admin Level User' : 'Create Admin Level User'}
        </h1>
        <p className="text-sm text-ink-secondary">
          {isEdit
            ? 'Update this administrator’s details.'
            : 'Register a new administrator to the CHUNAAV platform.'}
        </p>
      </div>

      {/* User Details card */}
      <div className="overflow-hidden rounded-xl border border-[#eef2f7] bg-white shadow-card">
        <div className="flex items-center gap-2.5 border-b border-[#e2e8f0] bg-[rgba(229,238,255,0.5)] px-6 py-4">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-brand-shade text-brand">
            <PersonIcon className="size-5" />
          </span>
          <h2 className="text-base font-semibold text-ink">User Details</h2>
        </div>

        <div className="flex flex-col gap-6 p-6">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <Field label="Full Name" required error={errorOf('fullName')}>
              <input
                name="fullName"
                value={values.fullName}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="e.g. Rajesh Kumar"
                className={inputCls(errorOf('fullName'))}
              />
            </Field>
            <Field label="Email Address" required error={errorOf('email')}>
              <input
                name="email"
                type="email"
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="rajesh.k@chunaav.gov.in"
                className={inputCls(errorOf('email'))}
              />
            </Field>

            <Field label="Contact Number" required error={errorOf('contact')}>
              <div
                className={[
                  'flex items-center rounded-lg border bg-white pl-4 transition-colors focus-within:ring-2',
                  errorOf('contact')
                    ? 'border-[#ef4444] focus-within:border-[#ef4444] focus-within:ring-red-500/15'
                    : 'border-[#e2e8f0] focus-within:border-brand focus-within:ring-brand/15',
                ].join(' ')}
              >
                <span className="shrink-0 pr-2 text-sm font-medium text-ink-secondary">+91</span>
                <input
                  name="contact"
                  value={values.contact}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="98765 43210"
                  className="w-full rounded-r-lg border-0 bg-transparent py-2.5 pr-4 text-sm text-ink placeholder:text-[#9ca3af] focus:outline-none"
                />
              </div>
            </Field>
            <Field label="Designation" required error={errorOf('designation')}>
              <input
                name="designation"
                value={values.designation}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="ABC"
                className={inputCls(errorOf('designation'))}
              />
            </Field>

            <Field label="Department" required error={errorOf('department')}>
              <input
                name="department"
                value={values.department}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="ABC"
                className={inputCls(errorOf('department'))}
              />
            </Field>
            <Field label="Organization" required error={errorOf('organization')}>
              <Select
                className="w-full"
                placeholder="Select organization"
                showSearch
                optionFilterProp="label"
                status={errorOf('organization') ? 'error' : undefined}
                value={values.organization === '' ? undefined : values.organization}
                onChange={(v) => {
                  setFieldValue('organization', v ?? '');
                  setFieldValue('level', ''); // levels belong to the chosen org
                }}
                onBlur={() => formik.setFieldTouched('organization', true)}
                options={(orgs?.items ?? []).map((o) => ({ value: o.id, label: o.name }))}
              />
            </Field>

            <Field label="Administrative Level" required error={errorOf('level')}>
              <TreeSelect
                className="w-full"
                placeholder={values.organization ? 'Select level' : 'Select an organization first'}
                disabled={!values.organization}
                status={errorOf('level') ? 'error' : undefined}
                showSearch
                treeNodeFilterProp="title"
                treeDefaultExpandAll
                treeLine={{ showLeafIcon: false }}
                treeData={levelTreeData}
                value={values.level || undefined}
                onChange={(v) => setFieldValue('level', v ?? '')}
                onBlur={() => formik.setFieldTouched('level', true)}
              />
            </Field>
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-[#e2e8f0] pt-6">
            <button
              type="button"
              onClick={goBack}
              className="rounded-lg border border-[#cbd5e1] bg-white px-6 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-brand-shade"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 rounded-lg bg-brand px-6 py-2.5 text-sm font-medium text-white shadow-btn transition-colors hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? 'Saving…' : isEdit ? 'Update User' : 'Save User'}
              <CheckCircleIcon className="size-4" />
            </button>
          </div>
        </div>
      </div>
    </form>
  );
}
